import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { visible } from "@/lib/extensions";
import { Upload, X, Check, Plus } from "lucide-react";

const chip = (on) => `px-4 py-1.5 rounded-full text-sm border transition-colors ${on ? "bg-[#0B192C] text-white border-[#0B192C]" : "border-slate-200 text-slate-600 hover:border-slate-400"}`;

export function ChipGroup({ opts, value = [], onChange, lang, testid }) {
  const toggle = (v) => onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {opts.map((o) => {
        const v = typeof o === "string" ? o : o.v;
        const l = typeof o === "string" ? o : o.l[lang];
        return <button key={v} type="button" onClick={() => toggle(v)} className={chip(value.includes(v))} data-testid={`${testid}-${v}`}>{l}</button>;
      })}
    </div>
  );
}

export function RadioGroup({ opts, value, onChange, lang, testid }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {opts.map((o) => (
        <button key={o.v} type="button" onClick={() => onChange(o.v)} className={chip(value === o.v)} data-testid={`${testid}-${o.v}`}>{o.l[lang]}</button>
      ))}
    </div>
  );
}

export function EmailList({ value = [], onChange, testid }) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const e = draft.trim().toLowerCase();
    if (e && /\S+@\S+\.\S+/.test(e) && !value.includes(e)) onChange([...value, e]);
    setDraft("");
  };
  return (
    <div>
      <div className="flex gap-2">
        <input className="input-elegant" type="email" value={draft} onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} placeholder="nombre@email.com" data-testid={`${testid}-input`} />
        <button type="button" onClick={add} className="btn-outline text-sm px-4" data-testid={`${testid}-add`}><Plus className="h-4 w-4" /></button>
      </div>
      <div className="flex flex-wrap gap-2 mt-3">
        {value.map((e) => (
          <span key={e} className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#F8F9FA] border border-slate-200 text-sm" data-testid={`${testid}-item`}>
            {e}<button type="button" onClick={() => onChange(value.filter((x) => x !== e))}><X className="h-3 w-3" /></button>
          </span>
        ))}
      </div>
    </div>
  );
}

export function DocUpload({ kind, value, onChange, testid }) {
  const [busy, setBusy] = useState(false);
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post(`/profile/documents?kind=${kind}`, fd);
      onChange({ id: data.id, filename: data.filename });
    } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-3">
      <label className={`btn-outline text-sm inline-flex items-center gap-2 cursor-pointer ${busy ? "opacity-50" : ""}`} data-testid={testid}>
        <Upload className="h-4 w-4" /> {value ? "Cambiar" : "Subir archivo"}
        <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={onFile} disabled={busy} />
      </label>
      {value && <span className="text-sm text-slate-600 inline-flex items-center gap-1" data-testid={`${testid}-name`}><Check className="h-4 w-4 text-emerald-600" />{value.filename}</span>}
    </div>
  );
}

export function BoatPicker({ value, onChange, testid }) {
  const [boats, setBoats] = useState(null);
  useEffect(() => { api.get("/boats").then((r) => setBoats(r.data)).catch(() => setBoats([])); }, []);
  if (boats === null) return <div className="h-10 w-48 bg-slate-100 rounded animate-pulse" />;
  return (
    <div className="space-y-2">
      <select className="input-elegant" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}>
        <option value="">—</option>
        {boats.map((b) => <option key={b.id} value={b.id}>{b.brand} {b.model} · {b.year} · {b.location}</option>)}
      </select>
      <a href="/mis-embarcaciones/nueva" className="text-xs font-display text-[#C2A878] hover:underline" data-testid={`${testid}-new`}>+ Registrar una embarcación nueva</a>
    </div>
  );
}

export function Field({ f, data, setData, lang, prefix }) {
  const { t } = useLang();
  if (!visible(f.showIf, data)) return null;
  const v = data[f.k];
  const set = (val) => setData((d) => ({ ...d, [f.k]: val }));
  const testid = `${prefix}-${f.k}`;
  let control;
  switch (f.t) {
    case "text": control = <input className="input-elegant" value={v || ""} onChange={(e) => set(e.target.value)} data-testid={testid} />; break;
    case "number": control = <input type="number" min={f.min} max={f.max} className="input-elegant max-w-xs" value={v ?? ""} onChange={(e) => set(e.target.value === "" ? null : Number(e.target.value))} data-testid={testid} />; break;
    case "chips": control = <ChipGroup opts={f.opts} value={v || []} onChange={set} lang={lang} testid={testid} />; break;
    case "radio": control = <RadioGroup opts={f.opts} value={v} onChange={set} lang={lang} testid={testid} />; break;
    case "toggle": control = (
      <button type="button" onClick={() => set(!v)} className={`flex items-center gap-3 text-left ${v ? "text-[#0B192C]" : "text-slate-500"}`} data-testid={testid}>
        <span className={`h-6 w-11 rounded-full transition-colors relative ${v ? "bg-[#0B192C]" : "bg-slate-200"}`}>
          <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${v ? "translate-x-5" : "translate-x-0.5"}`} />
        </span>
        <span className="text-sm">{f.l[lang]}</span>
      </button>); break;
    case "emails": control = <EmailList value={v || []} onChange={set} testid={testid} />; break;
    case "doc": control = <DocUpload kind={f.k} value={v} onChange={set} testid={testid} />; break;
    case "boat": control = <BoatPicker value={v} onChange={set} testid={testid} />; break;
    default: control = null;
  }
  return (
    <div>
      {f.t !== "toggle" && (
        <label className="label-elegant">{f.l[lang]}{f.req && <span className="text-[#C2A878]"> *</span>}</label>
      )}
      {control}
    </div>
  );
}

export function missingRequired(sections, data) {
  const missing = [];
  sections.forEach((s) => {
    if (!visible(s.showIf, data)) return;
    s.fields.forEach((f) => {
      if (!f.req || !visible(f.showIf, data)) return;
      const v = data[f.k];
      if (v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0)) missing.push(f);
    });
  });
  return missing;
}
